import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, List, Grid3x3 } from 'lucide-react'
import { Card } from '../ui/Card'
import { Timestamp } from '../ui/Timestamp'

/**
 * Your rhythm (2026-09-15) — when you do things, when you are around, when
 * you are quiet, in your own timezone.
 *
 * Owner: *"I want to be able to see when I am most active, when I am
 * inactive, when I opened any item or even the app … to determine my
 * efficient time slots or days or those awesome periods where I tend to work
 * well."* Two signals: DONE (a task or habit completed, a check-in, a message
 * to Horton, an ask answered) and PRESENT (the app or an item opened). The
 * model names peaks only when it has enough to name them and says how many
 * days of signal it holds. Words: quiet days, never inactive.
 *
 * Chart: one hue, light → dark, for magnitude (done per hour); a ring for
 * present-only. One series, so no legend box — the caption names the marks.
 * A list view stands in for the table.
 */
interface RhythmDay {
  date: string; weekday: string; state: 'active' | 'present' | 'quiet'
  done: number; present: number; kinds: Record<string, number>
}
interface Rhythm {
  timezone: string; timezone_from_profile?: boolean; days: number; since: string; today: string
  signal_days: number; totals: { done: number; present: number }
  kinds: Record<string, { side: string; label: string; count: number }>
  last_done_at: string | null; last_present_at: string | null; quiet_days_now: number | null
  day_counts: { active: number; present_only: number; quiet: number }
  by_day: RhythmDay[]
  heatmap: { done: number[][]; present: number[][] }
  peaks: { ready: boolean; needs?: string; best_block?: { start_hour: number; end_hour: number; done: number }; best_weekdays?: string[]; quiet_weekdays?: string[] }
  words: string[]
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
// Sequential ramp: one hue, light → dark (Tailwind needs the full class names in source).
const DONE_SHADE = ['bg-surface-alt', 'bg-brand/25', 'bg-brand/45', 'bg-brand/70', 'bg-brand']
const shadeFor = (n: number) => DONE_SHADE[n <= 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : n <= 4 ? 3 : 4]
const hourWord = (h: number) => `${h % 12 || 12} ${h < 12 ? 'am' : 'pm'}`

export function RhythmSection() {
  const [data, setData] = useState<Rhythm | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asList, setAsList] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/user-analytics/rhythm?days=28', { credentials: 'include' })
      .then(async r => {
        if (!r.ok) throw new Error(String(r.status))
        const j = await r.json()
        if (!j.success) throw new Error('failed')
        if (!cancelled) setData(j as Rhythm)
      })
      .catch(() => { if (!cancelled) setError("We couldn't read your rhythm just now.") })
    return () => { cancelled = true }
  }, [])

  const maxDone = data ? Math.max(0, ...data.heatmap.done.flat()) : 0

  return (
    <Card className="p-6 mb-8" data-testid="rhythm-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
          <Clock className="w-5 h-5 text-brand" />
          Your rhythm
          {data && <span className="text-app-micro font-normal text-muted">· {data.timezone}</span>}
        </h3>
        {data && (
          <button
            type="button"
            onClick={() => setAsList(v => !v)}
            className="flex items-center gap-1 text-app-micro text-brand hover:underline"
          >
            {asList ? <Grid3x3 className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}
            {asList ? 'Show the grid' : 'Show as a list'}
          </button>
        )}
      </div>

      {error && <p className="text-secondary">{error}</p>}
      {!data && !error && <p className="text-secondary">Reading your signals…</p>}

      {data && (
        <>
          <p className="text-secondary">{data.words.join(' ')}</p>
          {data.timezone_from_profile === false && (
            <p className="text-app-micro text-muted mt-1">
              Shown in UTC because your profile has no timezone yet. <Link to="/profile" className="text-brand hover:underline">Set it in your profile</Link> to see this in your own hours.
            </p>
          )}

          {/* The last 28 days, one square each: filled = something done, ring = around, empty = quiet */}
          <div className="mt-4">
            <div className="flex flex-wrap gap-1" aria-label="The last 28 days">
              {data.by_day.map(d => (
                <div key={d.date} className="flex flex-col items-center gap-0.5" title={`${d.weekday} ${d.date} · ${d.done} done · ${d.present} around`}>
                  <div
                    className={`h-4 w-4 rounded-sm ${
                      d.state === 'active' ? 'bg-brand'
                        : d.state === 'present' ? 'bg-brand/15 ring-1 ring-inset ring-brand/60'
                          : 'bg-surface-alt'
                    }`}
                  />
                  <span className="text-[9px] leading-none text-muted">{d.weekday[0]}</span>
                </div>
              ))}
            </div>
            <p className="text-app-micro text-muted mt-1">
              {data.day_counts.active} active · {data.day_counts.present_only} around but nothing done · {data.day_counts.quiet} quiet, of the last {data.days} days.
            </p>
          </div>

          {asList ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-app-micro text-muted">
                    <th className="py-1 pr-3 font-medium">Day</th>
                    <th className="py-1 pr-3 font-medium">State</th>
                    <th className="py-1 pr-3 font-medium">Done</th>
                    <th className="py-1 pr-3 font-medium">Around</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.by_day].reverse().map(d => (
                    <tr key={d.date} className="border-t border-soft">
                      <td className="py-1 pr-3 text-primary">{d.weekday} {d.date}</td>
                      <td className="py-1 pr-3 text-secondary">{d.state === 'present' ? 'around' : d.state}</td>
                      <td className="py-1 pr-3 text-primary">{d.done}</td>
                      <td className="py-1 pr-3 text-secondary">{d.present}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-5">
              <div className="text-app-micro text-muted mb-1">Hour of the day, by weekday — darker is more done; a ring means you were around.</div>
              <div className="overflow-x-auto overflow-y-hidden pb-1">
                <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: '2.25rem repeat(24, minmax(0.7rem, 1fr))' }}>
                  {data.heatmap.done.map((row, w) => (
                    <div key={WEEKDAYS[w]} className="contents">
                      <div className="text-app-micro text-muted pr-1 self-center">{WEEKDAYS[w]}</div>
                      {row.map((n, h) => {
                        const present = data.heatmap.present[w][h]
                        const isMax = n > 0 && n === maxDone
                        return (
                          <div
                            key={h}
                            title={`${WEEKDAYS[w]} ${hourWord(h)}–${hourWord((h + 1) % 24)} · ${n} done · ${present} around`}
                            className={`h-4 rounded-[3px] ${shadeFor(n)} ${n === 0 && present > 0 ? 'ring-1 ring-inset ring-brand/50' : ''} flex items-center justify-center`}
                          >
                            {isMax && <span className="text-[9px] leading-none font-semibold text-on-brand">{n}</span>}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                  <div />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="text-[9px] leading-none text-muted text-center">
                      {h % 6 === 0 ? hourWord(h).replace(' ', '') : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-app-micro text-muted">
            <span>From {data.signal_days} day{data.signal_days === 1 ? '' : 's'} of signal.</span>
            {data.last_done_at && <span>Last thing done <Timestamp value={data.last_done_at} /></span>}
            {data.last_present_at && <span>Last around <Timestamp value={data.last_present_at} /></span>}
            {!data.peaks.ready && data.peaks.needs && <span>Peaks appear after {data.peaks.needs}.</span>}
          </div>
        </>
      )}
    </Card>
  )
}

export default RhythmSection
